import React from 'react'
import { format } from 'date-fns'
import { createContext } from 'lib/createContext'
import { computeEndDate } from '../utils/computeEndDate'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useUnpaidShifts } from 'queries/unpaidShifts/useUnpaidShifts'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { useFiltersQueryState } from '../hooks/useFiltersQueryState'
import { GetUnpaidShiftsResponse } from 'api/unpaidShifts'
import { WorkerClassification } from 'constants/workerClassification'

type UnpaidShiftsContextState = {
  state: {
    data: GetUnpaidShiftsResponse
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
    filters: {
      userId: number | null
      shiftId: number | null
      companyId: number | null
      status: string[] | null
      workerClassification: WorkerClassification[] | null
      completedDaysAgo: number | null
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setFilters: ReturnType<typeof useFiltersQueryState>[1]
  }
}

const [Context, useUnpaidShiftsContextValue] =
  createContext<UnpaidShiftsContextState>({
    name: 'UnpaidShiftsContext',
  })

export const UnpaidShiftsProvider = ({
  children,
  initialUserId,
}: {
  children: React.ReactNode
  initialUserId?: number
}) => {
  const [pagination, setPagination] = usePaginationQueryState({
    pageIndexParser: 'integer',
  })
  const [filters, setFilters] = useFiltersQueryState()

  // Override userId filter if initialUserId is provided
  const effectiveFilters = React.useMemo(() => {
    if (initialUserId !== undefined) {
      return {
        ...filters,
        userId: initialUserId,
      }
    }
    return filters
  }, [filters, initialUserId])

  // Compute the "completed before" date when the filter is enabled.
  // This filters to shifts that completed before the start of the day N days ago (local time).
  // For example, if today is Feb 11 and completedDaysAgo is 2, this returns "2026-02-09",
  // filtering to shifts completed before Feb 9th (i.e., completed 2+ full days ago).
  // We include today's date string as a dependency so if the page is open across midnight,
  // the next render will recompute with the new date.
  const today = new Date()
  const todayStr = format(today, 'yyyy-MM-dd')
  const endDate = React.useMemo(
    () => computeEndDate(effectiveFilters.completedDaysAgo, today),
    [effectiveFilters.completedDaysAgo, todayStr]
  )

  const {
    data,
    isPending: isUnpaidShiftsLoading,
    isError: isUnpaidShiftsError,
    error: unpaidShiftsError,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useUnpaidShifts({
    userId: effectiveFilters.userId ?? undefined,
    shiftId: effectiveFilters.shiftId ?? undefined,
    companyId: effectiveFilters.companyId ?? undefined,
    status: (effectiveFilters.status?.filter(Boolean) as string[]) ?? undefined,
    type: effectiveFilters.workerClassification?.filter(Boolean) ?? undefined,
    endDate,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
  })

  if (isUnpaidShiftsLoading) {
    return <Loading />
  }

  if (isUnpaidShiftsError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  const value = {
    state: {
      data: data ?? {
        items: [],
        totalRows: 0,
        totalPages: 0,
        currentPage: 0,
        pageSize: pagination.pageSize,
      },
      dataUpdatedAt,
      isFetching,
      isError: isUnpaidShiftsError,
      error: parseErrorMessage(unpaidShiftsError),
      pagination,
      filters: effectiveFilters,
    },
    actions: {
      refetch,
      setPagination,
      setFilters,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useUnpaidShiftsContextValue }
